import { memo, useCallback, useMemo } from 'react';
import { Button, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import UserChip from 'containers/components/chips/UserChip';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import { logMarketingCampaignsEvent } from 'containers/campaigns/marketing/utils/marketingTracking';
import { getMomentFromDateBackend } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import {
    formatMarketingCampaignMetricLabel,
    formatMarketingCampaignMetricLabelOnly,
    getMarketingCampaignDetailGlobalMetrics,
    getMarketingCampaignDetailIndividualMetrics,
    getMarketingCampaignOrigin,
} from '../../../../utils/marketingCampaignActivity';

import '../../../shared/MarketingCampaignActivityContent.scss';
import '../../styles.scss';

const DetailMarketingCampaign = memo(({ activity, isFromWidgetDetail = false }) => {
    const theme = useTheme();
    const { hasTabsEnabled, openTab } = useEntityDetail();

    const campaign = activity?.Campaign;
    const campaignId = activity?.Id;
    const campaignSubject = campaign?.Subject;
    const origin = useMemo(() => getMarketingCampaignOrigin(activity), [activity]);
    const metrics = useMemo(
        () =>
            isFromWidgetDetail
                ? getMarketingCampaignDetailIndividualMetrics(campaign?.IndividualMetrics)
                : getMarketingCampaignDetailGlobalMetrics(campaign?.GlobalMetrics),
        [campaign?.GlobalMetrics, campaign?.IndividualMetrics, isFromWidgetDetail],
    );
    const sentDate = useMemo(() => {
        if (!activity?.ActivityDate) return null;

        const activityDate = getMomentFromDateBackend(activity.ActivityDate, null, true);
        return getLiteralWithParameters('label_date_with_time', [
            activityDate.format('LL'),
            activityDate.format('LT'),
        ]);
    }, [activity?.ActivityDate]);

    const handleViewCampaignDetails = useCallback(() => {
        if (!campaignId) return;

        logMarketingCampaignsEvent({
            submodule: 'campaign',
            functionality: 'detailViewFromActivity',
        });

        if (hasTabsEnabled) {
            openTab({
                entity: CAMPAIGNS_MARKETING,
                id: campaignId,
                avoidRedirects: true,
            });
            return;
        }

        ensureRoute(`${CAMPAIGNS_MARKETING.route}/${campaignId}`);
    }, [campaignId, hasTabsEnabled, openTab]);

    const metricsContent = useMemo(
        () => (
            <div className="marketing-campaign-card__metrics">
                {metrics.map(({ key, icon, value, labelKey, showValue = true }) => (
                    <div key={key} className="marketing-campaign-card__metric">
                        <Icon name={icon} size="medium" color={theme.colors.grey[500]} />
                        <Text type="body" color={theme.colors.utility.textSecondary}>
                            {showValue
                                ? formatMarketingCampaignMetricLabel(value, labelKey)
                                : formatMarketingCampaignMetricLabelOnly(labelKey)}
                        </Text>
                    </div>
                ))}
            </div>
        ),
        [metrics, theme.colors.grey, theme.colors.utility.textSecondary],
    );

    const rows = useMemo(
        () =>
            [
                {
                    key: 'subject',
                    label: getLiteral('label_subject'),
                    value: campaignSubject,
                },
                {
                    key: 'metrics',
                    label: getLiteral('label_performance'),
                    content: (
                        <div className="fm-activities-marketing-campaign__row-value">
                            {metricsContent}
                        </div>
                    ),
                    alwaysShow: true,
                },
                {
                    key: 'sent-date',
                    label: getLiteral('label_sent_date'),
                    value: sentDate,
                },
                {
                    key: 'origin',
                    label: getLiteral(origin.labelKey),
                    content: origin.name ? (
                        <UserChip idUser={origin.id} remotePopover={true}>
                            <Text className="fm-activities-marketing-campaign__row-value fm-activities-marketing-campaign__row-value--sender">
                                {origin.name}
                            </Text>
                        </UserChip>
                    ) : null,
                },
                {
                    key: 'sender',
                    label: getLiteral('label_sender'),
                    value: campaign?.SenderName,
                },
                // Temporarily hidden: BE returns an incorrect value for Campaign.Sender
                // (sender email). Re-enable once the API payload is fixed.
                // {
                //     key: 'sender-email',
                //     label: getLiteral('label_email_address'),
                //     value: campaign?.Sender,
                // },
            ].filter(
                ({ value, content, alwaysShow }) =>
                    alwaysShow || (value != null && value !== '') || content != null,
            ),
        [
            // campaign?.Sender, // Temporarily unused while sender-email row is hidden
            campaign?.SenderName,
            campaignSubject,
            metricsContent,
            origin.id,
            origin.labelKey,
            origin.name,
            sentDate,
        ],
    );

    if (!campaign) return null;

    return (
        <div className="fm-activities-marketing-campaign">
            <div className="fm-activities-marketing-campaign__header">
                <Text medium>{getLiteral('label_key_data')}</Text>
            </div>
            <div className="fm-activities-marketing-campaign__rows">
                {rows.map(({ key, label, value, content }) => (
                    <div key={key} className="fm-activities-marketing-campaign__row">
                        <Text
                            color={theme.colors.utility.textSecondary}
                            className="fm-activities-marketing-campaign__row-label"
                        >
                            {label}
                        </Text>
                        {content || (
                            <Text className="fm-activities-marketing-campaign__row-value">
                                {value}
                            </Text>
                        )}
                    </div>
                ))}
            </div>

            {campaignId && (
                <div className="fm-activities-marketing-campaign__actions">
                    <Button icon="launch" onClick={handleViewCampaignDetails}>
                        {getLiteral('label_view_campaign_details')}
                    </Button>
                </div>
            )}
        </div>
    );
});

DetailMarketingCampaign.displayName = 'DetailMarketingCampaign';

export default DetailMarketingCampaign;
